import React, { useState, useEffect, useRef, useCallback } from "react"
import { library } from "@fortawesome/fontawesome-svg-core"
import { fas }     from "@fortawesome/free-solid-svg-icons"
library.add(fas)

import { getRequest, pathname } from "@react/helpers/fetch"

import Card             from "@react/shared/ui/Card"
import Header           from "./components/Header"
import StatsStrip       from "./components/StatsStrip"
import Toolbar          from "./components/Toolbar"
import TemplatesTable   from "./components/TemplatesTable"
import Pagination       from "./components/Pagination"
import UseTemplateModal from "./components/UseTemplateModal"

// Entry point for the project-quote-template library — a close clone of
// the Ouvrages (qline_service_blueprints) index. The server hands over the
// initial payload; search / sort / page changes refetch the same controller
// action as JSON and swap data in place.
const DEBOUNCE_MS = 300

const App = ({ data: initialData }) => {
  const [data,      setData]      = useState(initialData)
  const [search,    setSearch]    = useState(initialData.search || "")
  const [page,      setPage]      = useState(initialData.pagination?.current_page || 1)
  const [order,     setOrder]     = useState(initialData.order)
  const [direction, setDirection] = useState(initialData.direction)
  const [loading,   setLoading]   = useState(false)

  // The template being instantiated in the "Utiliser" modal, or null.
  const [useModalTemplate, setUseModalTemplate] = useState(null)

  const indexUrl     = initialData.urls.index
  const didMountRef  = useRef(false)
  const debounceRef  = useRef(null)
  const requestIdRef = useRef(0)

  const fetchIndex = useCallback(() => {
    const requestId = ++requestIdRef.current
    const params    = { search, page, order, direction }

    setLoading(true)
    return getRequest(pathname({ path: indexUrl, format: "json", params })).then(json => {
      if (requestId !== requestIdRef.current) return
      setData(json)
      setLoading(false)
      window.history.replaceState(null, "", pathname({ path: indexUrl, params }))
    })
  }, [indexUrl, search, page, order, direction])

  useEffect(() => {
    if (!didMountRef.current) { didMountRef.current = true; return }
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(fetchIndex, DEBOUNCE_MS)
    return () => clearTimeout(debounceRef.current)
  }, [fetchIndex])

  const handleSearchChange = value => { setSearch(value); setPage(1) }
  const handleSortChange   = (nextOrder, nextDirection) => {
    setOrder(nextOrder); setDirection(nextDirection); setPage(1)
  }

  const handleNewClick = () => { window.location.href = data.urls.new_template }

  const openUseModal  = template => setUseModalTemplate(template)
  const closeUseModal = ()       => setUseModalTemplate(null)

  return (
    <div className="font-sans bg-slate-50 min-h-screen text-slate-900">
      <div className="max-w-[1500px] mx-auto px-8 py-8">
        <Header onNewClick={handleNewClick} />
        <StatsStrip stats={data.stats} />
        <Card className={`rounded-lg relative ${loading ? "opacity-75" : ""}`}>
          <Toolbar
            search         = {search}
            onSearchChange = {handleSearchChange}
            order          = {order}
            direction      = {direction}
            onSortChange   = {handleSortChange}
          />
          <TemplatesTable
            templates = {data.templates}
            onUse     = {openUseModal}
            onRefetch = {fetchIndex}
          />
          <Pagination
            pagination   = {data.pagination}
            onPageChange = {setPage}
          />
        </Card>
      </div>

      <UseTemplateModal
        isOpen                 = {useModalTemplate !== null}
        template               = {useModalTemplate}
        onClose                = {closeUseModal}
        clientsAutocompleteUrl = {data.urls.clients_autocomplete}
        sitesAutocompleteUrl   = {data.urls.sites_autocomplete}
      />
    </div>
  )
}

export default App
